import React, { useCallback, useState, useEffect } from 'react'
import { useIntl } from 'react-intl'

import Accordion from 'src/comps/Accordion'
import CreditCardForm from 'src/comps/PaymentForm/CreditCardForm'
import PaymentChangeConditions from 'src/comps/PaymentForm/PaymentChangeConditions'
import ApplePay from 'src/comps/ApplePay'
import PaymentMethodsLogos from 'src/comps/PaymentMethodsLogos'
import { CreditCardType } from 'src/types/checkout'
import { PaymentMethods } from 'client/contexts/Purchase/mappers'
import { useSession } from 'src/hooks/useSession'
import {
  hasApplePaySDK,
  PaymentFormType,
  PaymentTypes,
  PaymentTypeToNameMap,
} from 'src/comps/PaymentForm/utils'

import { CREDIT_CARD_PAYMENT_METHOD, ALTERNATIVE_PAYMENT_METHOD } from './constants'
import ButtonSimple from 'src/comps/ButtonSimple'
import styles from './styles.module.css'

const getPaymentFormType = (paymentMethods: PaymentMethods) => {
  if (paymentMethods.apm?.length || (paymentMethods.applePay && hasApplePaySDK())) {
    return PaymentFormType.Multiple
  }
  return PaymentFormType.Single
}

const getAPMButtonVariant = (apmId: string) => {
  if (apmId === PaymentTypes.Multibanco) {
    return 'tertiary'
  }

  return 'primary'
}

const getAPMName = (apmId: string) => {
  return PaymentTypeToNameMap[apmId as PaymentTypes]
}

interface PaymentFormProps {
  creditCard: CreditCardType
  disabled: boolean
  isPrime: boolean
  onFail: () => void
  onSuccess: (
    paymentMethod: string,
    paymentParams: { id: string; ephemeralKey?: string; publicKey?: string; paymentData?: string }
  ) => void
  paymentMethods: PaymentMethods
  purchaseId: string
}

const SinglePaymentForm = ({
  creditCard,
  disabled,
  isPrime,
  onFail,
  onSuccess,
  paymentMethods,
  purchaseId,
}: PaymentFormProps) => (
  <div className={`${styles.creditCardContainer} ${styles.singlePayment}`}>
    <CreditCardForm
      creditCard={creditCard}
      disabled={disabled}
      isPrime={isPrime}
      onError={onFail}
      onSuccess={onSuccess}
      paymentMethods={paymentMethods}
      purchaseId={purchaseId}
    />
  </div>
)

interface MultiplePaymentFormProps extends PaymentFormProps {
  onSelectedPaymentMethodChange: (pm: string) => void
  selectedPaymentMethod: string
  payNow?: number
  cancellationDate?: string
  sellingRate?: number
}

export const MultiplePaymentForm = ({
  creditCard,
  disabled,
  isPrime,
  onFail,
  onSuccess,
  paymentMethods,
  purchaseId,
  onSelectedPaymentMethodChange,
  selectedPaymentMethod,
  payNow,
  cancellationDate,
  sellingRate,
}: MultiplePaymentFormProps) => {
  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false)
  const handleApmClick = useCallback(
    (apmId: string) => {
      onSuccess(ALTERNATIVE_PAYMENT_METHOD, { id: apmId })
    },
    [onSuccess]
  )

  useEffect(() => {
    if (paymentMethods.applePay) {
      setIsApplePayAvailable(hasApplePaySDK())
    }
  }, [paymentMethods.applePay])

  const intl = useIntl()

  const apms = paymentMethods.apm?.map(({ kind, value }) => {
    const APMButtonVariant = getAPMButtonVariant(kind)
    return {
      id: value,
      title: [
        <PaymentMethodsLogos apm={kind} key="logo" />,
        <span key="text">{getAPMName(kind)}</span>,
      ],
      body: (
        <div
          className={`${styles.apmSubmitButtonContainer} ${
            APMButtonVariant === 'tertiary' && styles.tertiary
          }`}
        >
          <PaymentChangeConditions
            sellingRate={sellingRate}
            cancellationDate={cancellationDate}
            payNow={payNow}
          />
          <ButtonSimple
            dataTestId={`apm-submit-${kind}`}
            onClick={event => {
              event.preventDefault()
              handleApmClick(value)
            }}
            text={intl.formatMessage({ id: 'global.pay-with' })}
            icon={<PaymentMethodsLogos apm={kind} width={50} height={40} />}
            variant={APMButtonVariant}
          />
        </div>
      ),
    }
  })

  return (
    <Accordion
      onChange={onSelectedPaymentMethodChange}
      selectedSectionId={selectedPaymentMethod}
      sections={[
        ...(isApplePayAvailable
          ? [
              {
                id: 'applePay',
                title: [
                  <PaymentMethodsLogos applePay key="apLogo" />,
                  <span key="apText">Apple Pay</span>,
                ],
                body: (
                  <div className={styles.applePayButtonContainer}>
                    <ApplePay
                      onSuccess={onSuccess}
                      onError={onFail}
                      purchaseId={purchaseId}
                      payNow={payNow ?? sellingRate}
                    />
                  </div>
                ),
              },
            ]
          : []),
        {
          id: CREDIT_CARD_PAYMENT_METHOD,
          title: [
            <PaymentMethodsLogos creditCard key="ccLogo" />,
            <span key="ccText">Credit / Debit Card</span>,
          ],
          body: (
            <div className={styles.creditCardContainer}>
              <CreditCardForm
                creditCard={creditCard}
                disabled={disabled}
                isPrime={isPrime}
                onError={onFail}
                onSuccess={onSuccess}
                paymentMethods={paymentMethods}
                purchaseId={purchaseId}
              />
            </div>
          ),
        },
        ...(apms || []),
      ]}
    />
  )
}

const PaymentFormComponentDictionary = {
  [PaymentFormType.Single]: SinglePaymentForm,
  [PaymentFormType.Multiple]: MultiplePaymentForm,
}

interface Props {
  creditCard: CreditCardType
  disabled: boolean
  onFail: () => void
  onSuccess: (paymentMethod: string, paymentParams: { id: string }) => void
  paymentMethods: PaymentMethods
  purchaseId: string
  show: boolean
  onSelectedPaymentMethodChange: (pm: string) => void
  selectedPaymentMethod: string
  payNow?: number
  cancellationDate?: string
  sellingRate?: number
}

const PaymentData = ({
  creditCard,
  disabled,
  onFail,
  onSuccess,
  paymentMethods,
  purchaseId,
  show,
  onSelectedPaymentMethodChange,
  selectedPaymentMethod,
  payNow,
  cancellationDate,
  sellingRate,
}: Props) => {
  const paymentFormType = getPaymentFormType(paymentMethods)
  const PaymentDataForm = PaymentFormComponentDictionary[paymentFormType]

  const { currentUserHasPrimeSubscription } = useSession()

  if (show) {
    return null
  }

  return (
    <PaymentDataForm
      creditCard={creditCard}
      disabled={disabled}
      isPrime={currentUserHasPrimeSubscription}
      onFail={onFail}
      onSuccess={onSuccess}
      paymentMethods={paymentMethods}
      purchaseId={purchaseId}
      onSelectedPaymentMethodChange={onSelectedPaymentMethodChange}
      selectedPaymentMethod={selectedPaymentMethod}
      payNow={payNow}
      cancellationDate={cancellationDate}
      sellingRate={sellingRate}
    />
  )
}

export default PaymentData
